import { useState, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import Selectable from 'components/common/Selectable/Selectable';
import { useDebounce, useFetchBucketFiles, useVariable } from 'hooks';
import { Modal, ModalBody, Button, ModalHeader, Spinner } from 'components/UI';
import Search from 'components/common/Search/Search';
import { checkIfError } from 'utils/helpers';
import { IBucketFile, IBucketFileFetchRequest } from '../../../../server/types';

export type FileBucketSelectProps = {
  bucketName?: string;
  fileType?: 'zip' | 'doc';
  isZip?: boolean;
  limit?: number;
  onCancel(): void;
  onChange(args0: string, arg1: boolean): void;
};

const FileBucketSelect = ({
  bucketName,
  onCancel,
  onChange,
  fileType,
  isZip = true,
  limit = 50,
}: FileBucketSelectProps) => {
  const [fetchBucketFiles] = useFetchBucketFiles();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<IBucketFile[]>([]);
  const [search, setSearch] = useState<string>();
  const [total, setTotal] = useVariable<number>();
  const [debouncedValue, setDebouncedValue] = useDebounce<string | undefined>(search);

  const handleClear = () => {
    setSearch('');
    setDebouncedValue('');
  };

  const handleFetchFiles = async (params?: IBucketFileFetchRequest) => {
    if (!bucketName) {
      return;
    }

    setLoading(!params?.skip);
    const bucketFilesResult = await fetchBucketFiles(bucketName, { ...params, key: debouncedValue, limit, fileType });

    if (checkIfError(bucketFilesResult)) return;

    setFiles(
      params?.skip
        ? (prevFiles) => [...(prevFiles ?? []), ...bucketFilesResult.bucketFiles]
        : bucketFilesResult.bucketFiles
    );
    setTotal(bucketFilesResult.total);
    setLoading(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

  useEffect(() => {
    handleFetchFiles();
  }, [debouncedValue]);

  const fileList = files ? files.map(({ key }) => key) : null;
  const renderSelectable = loading ? (
    <Flex justifyContent="center" pb={5}>
      <Spinner />
    </Flex>
  ) : (
    <Box pb={2}>
      <Selectable
        options={fileList}
        scrollProps={{
          hasMore: total > files.length,
          onLoadMore: () => handleFetchFiles({ skip: files.length }),
        }}
        onChange={(file: string) => onChange(file, isZip)}
      />
      <Flex justifyContent="center" mt={5} mb={2}>
        <Button onClick={onCancel} size="sm">
          CANCEL
        </Button>
      </Flex>
    </Box>
  );

  return (
    <Modal isOpen isCloseButton closeOnEsc={false} closeOnOverlayClick isCentered size="lg" onClose={onCancel}>
      <ModalHeader textAlign="center" fontSize="xl" pb={0}>
        Select a File
      </ModalHeader>
      <ModalBody>
        <Box px={6} py={4}>
          <Search
            name="search"
            value={search}
            onChange={handleChange}
            onClear={handleClear}
            placeholder="Search bucket files by file name..."
            bgColor="base2Colour.500"
            _focus={{
              bgColor: 'base2Colour.500',
            }}
            _hover={{
              bgColor: 'base2Colour.500',
            }}
            fontSize={14}
            containerProps={{
              px: 4,
            }}
          />
        </Box>
        <Box>{renderSelectable}</Box>
      </ModalBody>
    </Modal>
  );
};

export default FileBucketSelect;
